import {
  InlineSpinner_default
} from "./chunk-KAWLIR3H.js";

// src/ApprovalCard.tsx
import { useEffect, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var humanizeIdentifier = (value) => String(value || "").split("_").filter(Boolean).map((word, index) => {
  if (word.toLowerCase() === "gmail") return "Gmail";
  return index === 0 ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word;
}).join(" ");
var getActionLabel = (action, functionCallName) => {
  const actionStart = functionCallName?.indexOf(action) ?? -1;
  const actionIdentifier = actionStart >= 0 && functionCallName ? functionCallName.slice(actionStart) : action;
  return humanizeIdentifier(actionIdentifier) || "Requested action";
};
var RISK_BADGE_STYLES = {
  SAFE: "bg-fill-green-100 text-ink-green-800 dark:bg-fill-green-600/20 dark:text-ink-green-300",
  MEDIUM: "bg-fill-hex-fef3c7 text-ink-amber-700 dark:bg-fill-hex-d97706/20 dark:text-ink-amber-300",
  HIGH_RISK: "bg-fill-red-100 text-ink-red-800 dark:bg-fill-red-600/20 dark:text-ink-red-300",
  CRITICAL: "bg-fill-red-100 text-ink-red-800 dark:bg-fill-red-600/20 dark:text-ink-red-300"
};
var ApprovalCard = ({
  cardData,
  onApprove,
  onCancel,
  onExpire,
  theme = "light",
  variant = "default"
}) => {
  const isDark = theme === "dark";
  const isCompact = variant === "compact";
  const {
    approval_id,
    agent_name,
    action,
    action_description,
    function_call_name,
    function_call_args,
    risk_level,
    confirmation_text,
    expires_at,
    status = "pending"
  } = cardData;
  const confirmationInputId = `approval-confirmation-${approval_id}`;
  const [inputValue, setInputValue] = useState("");
  const [timeRemaining, setTimeRemaining] = useState(0);
  const timerRef = useRef(null);
  const inputRef = useRef(null);
  const hasExpiredRef = useRef(false);
  useEffect(() => {
    if (status !== "pending") {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    const calculateTimeRemaining = () => {
      const now = Math.floor(Date.now() / 1e3);
      return Math.max(0, expires_at - now);
    };
    setTimeRemaining(calculateTimeRemaining());
    hasExpiredRef.current = false;
    timerRef.current = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
      if (remaining <= 0 && !hasExpiredRef.current) {
        hasExpiredRef.current = true;
        if (timerRef.current !== null) clearInterval(timerRef.current);
        onExpire?.(approval_id);
      }
    }, 1e3);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [expires_at, approval_id, onExpire, status]);
  const isConfirmationValid = inputValue === confirmation_text;
  const isProcessing = status === "processing" || status === "approved_waiting";
  const handleConfirm = () => {
    if (!isConfirmationValid || isProcessing) return;
    if (timerRef.current) clearInterval(timerRef.current);
    onApprove?.(approval_id, inputValue);
  };
  const handleCancel = () => {
    if (isProcessing) return;
    if (timerRef.current) clearInterval(timerRef.current);
    onCancel?.(approval_id);
  };
  const handleKeyDown = (event) => {
    if (event.key === "Enter" && isConfirmationValid) handleConfirm();
  };
  const isHighRisk = risk_level === "HIGH_RISK";
  const riskColor = isHighRisk ? "rgb(var(--photon-button-palette-red-600))" : "rgb(var(--photon-button-palette-amber-500))";
  const riskForeground = isHighRisk ? "rgb(var(--photon-ink-on-color))" : "rgb(var(--photon-ink-on-warning))";
  const riskBadgeClass = RISK_BADGE_STYLES[risk_level] || RISK_BADGE_STYLES.MEDIUM;
  const photonBlue = "var(--photon-chat-accent)";
  const actionLabel = getActionLabel(action, function_call_name);
  const query = function_call_args?.query_string || function_call_args?.query;
  const summary = function_call_args?.approval_display?.summary;
  const approvalSummary = Array.isArray(summary) ? summary.filter(
    (field) => field?.label && field?.value !== void 0 && field?.value !== null
  ) : [];
  const hasDetails = !!(action_description || function_call_name || function_call_args);
  const timerCenter = isCompact ? 20 : 24;
  const timerRadius = isCompact ? 15 : 18;
  const timerSizeClass = isCompact ? "w-10 h-10" : "w-12 h-12";
  const circumference = 2 * Math.PI * timerRadius;
  const strokeDashoffset = circumference * (1 - timeRemaining / 60);
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return minutes > 0 ? `${minutes}:${remainingSeconds.toString().padStart(2, "0")}` : `${remainingSeconds}s`;
  };
  if (status === "approved") return null;
  if (isProcessing) {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        role: "status",
        "aria-live": "polite",
        className: `rounded-lg border ${isCompact ? "p-3 my-1.5" : "p-4 my-2"} ${isDark ? "bg-fill-hex-2a2a2e border-edge-gray-700" : "bg-fill-white border-edge-gray-200 shadow-sm"}`,
        children: [
          /* @__PURE__ */ jsxs("div", { className: `flex items-center gap-2 ${isCompact ? "mb-2" : "mb-3"}`, children: [
            /* @__PURE__ */ jsxs("span", { className: "text-xs font-semibold px-2 py-1 rounded", style: { backgroundColor: "rgb(var(--photon-button-primary))", color: "rgb(var(--photon-button-on-primary))" }, children: [
              agent_name,
              " \u2022 ",
              actionLabel
            ] }),
            /* @__PURE__ */ jsx("span", { className: `text-xs font-semibold px-2 py-1 rounded ${riskBadgeClass}`, children: risk_level })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
            /* @__PURE__ */ jsx(InlineSpinner_default, { size: "md" }),
            /* @__PURE__ */ jsx("span", { className: `text-sm font-medium ${isDark ? "text-ink-gray-300" : "text-ink-gray-700"}`, children: cardData.processingText || (status === "approved_waiting" ? "Approved, waiting for other actions..." : "Executing agent...") })
          ] }),
          action_description && /* @__PURE__ */ jsx("div", { className: `mt-3 text-xs ${isDark ? "text-ink-gray-400" : "text-ink-gray-600"}`, children: action_description })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: `rounded-lg border ${isCompact ? "p-3 my-1.5" : "p-4 my-2"} ${isDark ? "bg-fill-hex-2a2a2e border-edge-gray-700" : "bg-fill-white border-edge-gray-200 shadow-sm"}`, children: [
    /* @__PURE__ */ jsxs("div", { className: `flex justify-between items-start ${isCompact ? "mb-2" : "mb-3"}`, children: [
      /* @__PURE__ */ jsx(
        "span",
        {
          className: "text-xs font-semibold px-2 py-1 rounded",
          style: { backgroundColor: riskColor, color: riskForeground },
          children: isHighRisk ? "\u26A0\uFE0F HIGH RISK" : "\u26A0\uFE0F APPROVAL REQUIRED"
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: `relative flex-shrink-0 ${timerSizeClass}`, children: [
        /* @__PURE__ */ jsxs("svg", { className: `transform -rotate-90 ${timerSizeClass}`, viewBox: `0 0 ${timerCenter * 2} ${timerCenter * 2}`, children: [
          /* @__PURE__ */ jsx("circle", { cx: timerCenter, cy: timerCenter, r: timerRadius, stroke: "var(--photon-chat-border)", strokeWidth: "4", fill: "none" }),
          /* @__PURE__ */ jsx(
            "circle",
            {
              cx: timerCenter,
              cy: timerCenter,
              r: timerRadius,
              stroke: timeRemaining <= 10 ? "var(--photon-alert-error)" : photonBlue,
              strokeWidth: "4",
              fill: "none",
              strokeLinecap: "round",
              strokeDasharray: circumference,
              strokeDashoffset,
              style: { transition: "stroke-dashoffset 1s linear" }
            }
          )
        ] }),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "absolute inset-0 flex items-center justify-center text-xs font-mono font-bold",
            style: { color: timeRemaining <= 10 ? "var(--photon-alert-error)" : photonBlue },
            children: formatTime(timeRemaining)
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: isCompact ? "mb-3 space-y-2" : "mb-4 space-y-3", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("div", { className: `text-[10px] font-semibold uppercase ${isDark ? "text-ink-gray-500" : "text-ink-gray-400"}`, children: "Agent" }),
        /* @__PURE__ */ jsx("div", { className: `text-sm font-semibold mt-0.5 ${isDark ? "text-ink-white" : "text-ink-gray-900"}`, children: agent_name })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: `border-l-2 pl-3 ${isDark ? "border-edge-gray-600" : "border-edge-gray-200"}`, children: [
        /* @__PURE__ */ jsx("div", { className: `text-[10px] font-semibold uppercase ${isDark ? "text-ink-gray-500" : "text-ink-gray-400"}`, children: "Action" }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3 mt-0.5", children: [
          /* @__PURE__ */ jsx("div", { className: `${isCompact ? "text-sm" : "text-base"} font-semibold ${isDark ? "text-ink-white" : "text-ink-gray-900"}`, children: actionLabel }),
          /* @__PURE__ */ jsx("span", { className: `flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide ${riskBadgeClass}`, children: risk_level })
        ] }),
        query && /* @__PURE__ */ jsxs("div", { className: "mt-2", children: [
          /* @__PURE__ */ jsx("div", { className: `text-[10px] font-semibold uppercase ${isDark ? "text-ink-gray-500" : "text-ink-gray-400"}`, children: "Query" }),
          /* @__PURE__ */ jsx("p", { className: `mt-0.5 text-xs font-mono break-words line-clamp-3 ${isDark ? "text-ink-gray-300" : "text-ink-gray-600"}`, children: query })
        ] }),
        approvalSummary.map((field, index) => /* @__PURE__ */ jsxs("div", { className: "mt-2", children: [
          /* @__PURE__ */ jsx("div", { className: `text-[10px] font-semibold uppercase ${isDark ? "text-ink-gray-500" : "text-ink-gray-400"}`, children: field.label }),
          /* @__PURE__ */ jsx("p", { className: `mt-0.5 text-sm font-medium break-words ${isDark ? "text-ink-gray-200" : "text-ink-gray-700"}`, children: String(field.value) })
        ] }, `${field.label}-${index}`))
      ] }),
      hasDetails && /* @__PURE__ */ jsxs("details", { className: `text-xs ${isDark ? "text-ink-gray-400" : "text-ink-gray-500"}`, children: [
        /* @__PURE__ */ jsx("summary", { className: "cursor-pointer font-medium select-none", children: "Details" }),
        /* @__PURE__ */ jsxs("div", { className: `mt-2 space-y-1.5 border-l pl-3 ${isDark ? "border-edge-gray-700" : "border-edge-gray-200"}`, children: [
          action_description && /* @__PURE__ */ jsx("div", { children: action_description }),
          function_call_name && /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("span", { className: "font-semibold", children: "Tool:" }),
            " ",
            /* @__PURE__ */ jsx("span", { className: "font-mono break-all", children: function_call_name })
          ] }),
          function_call_args && /* @__PURE__ */ jsx("pre", { className: "overflow-x-auto whitespace-pre-wrap break-words font-mono", children: JSON.stringify(function_call_args, null, 2) })
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: isCompact ? "mb-3" : "mb-4", children: [
      /* @__PURE__ */ jsxs("label", { htmlFor: confirmationInputId, className: `block text-sm mb-1.5 ${isDark ? "text-ink-gray-300" : "text-ink-gray-600"}`, children: [
        "Type ",
        /* @__PURE__ */ jsx("span", { className: "font-mono font-bold", style: { color: riskColor }, children: confirmation_text }),
        " to confirm:"
      ] }),
      /* @__PURE__ */ jsx(
        "input",
        {
          id: confirmationInputId,
          ref: inputRef,
          type: "text",
          value: inputValue,
          onChange: (event) => setInputValue(event.target.value),
          onKeyDown: handleKeyDown,
          disabled: isProcessing,
          placeholder: "Enter confirmation text",
          className: `w-full ${isCompact ? "h-9" : "h-10"} appearance-none rounded-md border border-solid px-3 text-sm shadow-none transition-colors focus:outline-none focus:ring-2 ${isDark ? "bg-fill-hex-2a2a2e border-edge-gray-600 text-ink-white placeholder-ink-gray-500" : "bg-fill-white border-edge-gray-300 text-ink-gray-900 placeholder-ink-gray-400"} ${isConfirmationValid ? "border-edge-green-500 focus:border-edge-green-500 focus:ring-focus-green-500/30" : "focus:border-edge-blue-500 focus:ring-focus-blue-500/30"}`,
          style: { borderColor: isConfirmationValid ? "rgb(var(--photon-edge-green-500))" : void 0 }
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { className: `flex ${isCompact ? "justify-end gap-2" : "gap-3"}`, children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: handleConfirm,
          disabled: !isConfirmationValid || isProcessing,
          className: `${isCompact ? "min-w-24 px-3 py-1.5" : "flex-1 px-4 py-2"} border-0 rounded-lg text-sm font-semibold transition-all ${isConfirmationValid && !isProcessing ? "text-ink-white hover:opacity-90 cursor-pointer" : `${isDark ? "bg-fill-gray-700 text-ink-gray-500" : "bg-fill-gray-200 text-ink-gray-400"} cursor-not-allowed`}`,
          style: {
            backgroundColor: isConfirmationValid && !isProcessing ? riskColor : void 0,
            color: isConfirmationValid && !isProcessing ? riskForeground : void 0
          },
          children: isProcessing ? "Processing..." : "Confirm"
        }
      ),
      /* @__PURE__ */ jsx(
        "button",
        {
          onClick: handleCancel,
          disabled: isProcessing,
          className: `${isCompact ? "min-w-24 px-3 py-1.5" : "flex-1 px-4 py-2"} border-0 rounded-lg text-sm font-semibold transition-all ${isDark ? "bg-fill-gray-700 text-ink-gray-300 hover:bg-fill-gray-600" : "bg-fill-gray-200 text-ink-gray-700 hover:bg-fill-gray-300"} ${isProcessing ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`,
          children: "Cancel"
        }
      )
    ] })
  ] });
};
var ApprovalCard_default = ApprovalCard;
export {
  ApprovalCard_default as default
};
